import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { UserPicker, type UserPickerOption } from '@/components/ui/user-picker';

import type { FederatedIdentityViewModel } from '../api';
import { usePlatformUserSearch } from '../usePlatformUserSearch';

/** A live user-typeahead hook: maps a query to picker options. The plane supplies its own search. */
export type UseUserSearch = (query: string) => {
  readonly results: readonly UserPickerOption[];
  readonly isLoading: boolean;
};

/**
 * Links a captured (PENDING) federated identity to an existing user (PTV2-1451). Shows the captured
 * display hints so the admin can identify the opaque subject, then a user typeahead. The link is
 * confirmed only when a user is selected — never auto-linked by email.
 *
 * Plane-agnostic: the user typeahead is injected via {@link UseUserSearch} (defaults to the global
 * search); the tenant console passes a tenant-scoped search so it links within one tenant (PTV2-1472).
 */
export function LinkIdentityDialog({
  open,
  identity,
  onOpenChange,
  onSubmit,
  pending,
  submitError,
  useUserSearch = usePlatformUserSearch,
}: {
  readonly open: boolean;
  readonly identity: FederatedIdentityViewModel | null;
  readonly onOpenChange: (next: boolean) => void;
  readonly onSubmit: (userGuid: string) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
  readonly useUserSearch?: UseUserSearch;
}): ReactElement {
  const { t } = useTranslation();
  const fp = 'routes.platform.federatedIdentities.link';

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<UserPickerOption | null>(null);
  const { results, isLoading } = useUserSearch(query);

  // Reseed the picker every time the dialog opens (or targets a different identity).
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelected(null);
    }
  }, [open, identity?.guid]);

  const summary = useMemo(
    () =>
      identity === null
        ? []
        : [
            { label: t(`${fp}.capturedName`), value: identity.capturedName },
            { label: t(`${fp}.capturedEmail`), value: identity.capturedEmail },
            { label: t(`${fp}.provider`), value: identity.providerIssuer },
            { label: t(`${fp}.subject`), value: identity.externalSubject },
          ].filter((row) => row.value.length > 0),
    [identity, t],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && pending) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t(`${fp}.title`)}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">{t(`${fp}.description`)}</p>

        <dl className="rounded-md border border-border bg-muted/40 divide-y divide-border text-sm">
          {summary.map((row) => (
            <div key={row.label} className="flex gap-3 px-3 py-2">
              <dt className="w-28 shrink-0 text-muted-foreground">{row.label}</dt>
              <dd className="min-w-0 break-all font-mono text-xs text-foreground/90">{row.value}</dd>
            </div>
          ))}
        </dl>

        <div className="space-y-1.5">
          <label htmlFor="link-identity-user" className="text-sm font-medium text-foreground">
            {t(`${fp}.userLabel`)}
          </label>
          <UserPicker
            id="link-identity-user"
            value={selected}
            onChange={setSelected}
            options={results}
            isLoading={isLoading}
            query={query}
            onQueryChange={setQuery}
            placeholder={t(`${fp}.userPlaceholder`)}
            ariaLabel={t(`${fp}.userLabel`)}
            disabled={pending}
          />
        </div>

        {submitError !== undefined && submitError !== null && (
          <p role="alert" className="text-sm text-red-600">
            {t(`${fp}.submitError`, { code: getErrorCodeForUi(submitError) })}
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={pending}
          >
            {t(`${fp}.cancel`)}
          </Button>
          <Button
            type="button"
            loading={pending}
            disabled={selected === null}
            onClick={() => {
              if (selected !== null) onSubmit(selected.guid);
            }}
          >
            {t(`${fp}.confirm`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
